/**
 * BOOKOO scale BLE protocol (Themis Ultra, also Themis Mini): pure encoding/decoding, no Web Bluetooth here.
 *
 * Source of truth: BOOKOO's official spec, github.com/BooKooCode/OpenSource `bookoo_ultra_scale/protocols.md`
 * (Sep 2026). See docs/bookoo-protocol.md for the full reference and the checksum-history caveat: always
 * compute the XOR checksum, never copy command bytes from older libraries.
 */

// ---------------------------------------------------------------- GATT layout

const BLUETOOTH_BASE_UUID_SUFFIX = '-0000-1000-8000-00805f9b34fb';

/** Expand a 16-bit UUID alias to its 128-bit form on the Bluetooth base UUID. */
export function uuidFrom16(alias: number): string {
  return `0000${alias.toString(16).padStart(4, '0')}${BLUETOOTH_BASE_UUID_SUFFIX}`;
}

export const BOOKOO_SERVICE = 0x0ffe;
/** Notify: weight / flow / battery frames (type 0x0B) and, on Ultra fw >= 4, 0x0D / 0x0F frames. */
export const BOOKOO_WEIGHT_CHAR = 0xff11;
/** Write: 6-byte command frames. May also notify on some firmware. */
export const BOOKOO_COMMAND_CHAR = 0xff12;

export const BOOKOO_SERVICE_UUID = uuidFrom16(BOOKOO_SERVICE);
export const BOOKOO_WEIGHT_CHAR_UUID = uuidFrom16(BOOKOO_WEIGHT_CHAR);
export const BOOKOO_COMMAND_CHAR_UUID = uuidFrom16(BOOKOO_COMMAND_CHAR);

// ---------------------------------------------------------------- device names

/**
 * Advertised name prefixes. Ultra: "BOOKOO_SC_U 614603", Mini: "BOOKOO_SC 536475".
 * Mini firmware 1.4.1 drops the leading B ("OOKOO_SC 645620").
 */
export const BOOKOO_NAME_PREFIXES = ['BOOKOO_SC', 'OOKOO_SC'] as const;

export type BookooModel = 'ultra' | 'mini' | 'unknown';

export function isBookooScaleName(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.trim().toUpperCase();
  return BOOKOO_NAME_PREFIXES.some((p) => n.startsWith(p));
}

/** Best-effort model detection from the advertised name ("_U" / " U" suffix = Ultra). */
export function bookooModelFromName(name: string | null | undefined): BookooModel {
  if (!name || !isBookooScaleName(name)) return 'unknown';
  const rest = name.trim().toUpperCase().replace(/^B?OOKOO_SC/, '');
  return /^[\s_-]*U(?![A-Z])/.test(rest) ? 'ultra' : 'mini';
}

// ---------------------------------------------------------------- framing

export const BOOKOO_PRODUCT_SCALE = 0x03;
export const BOOKOO_COMMAND_TYPE = 0x0a;
export const BOOKOO_COMMAND_LENGTH = 6;
export const BOOKOO_PACKET_LENGTH = 20;

export const BookooPacketType = {
  weight: 0x0b,
  /** Auto-mode event / settlement (Ultra beta fw >= 3.2.4, release fw >= 4.0.0). */
  event: 0x0d,
  /** Powder (dose) weight (Ultra beta fw >= 3.2.4, release fw >= 4.0.0). */
  powder: 0x0f,
} as const;

export const BookooCommandId = {
  tare: 0x01,
  beep: 0x02,
  autoOff: 0x03,
  startTimer: 0x04,
  stopTimer: 0x05,
  resetTimer: 0x06,
  tareAndStartTimer: 0x07,
  flowSmoothing: 0x08,
  powderWeight: 0x0d,
  /** Switch the scale off (Ultra fw >= 4.0.0; ignored while charging; not documented for the Mini). */
  shutdown: 0x15,
  /** Reset the auto-off countdown (Ultra fw >= 4.0.1). */
  keepAwake: 0x25,
} as const;

const SIGN_PLUS = 0x2b; // '+'
const SIGN_MINUS = 0x2d; // '-'

/** XOR of the first `length` bytes (default: all). The frame checksum is the XOR of every byte before it. */
export function xorChecksum(bytes: ArrayLike<number>, length: number = bytes.length): number {
  let x = 0;
  for (let i = 0; i < length; i++) x ^= (bytes[i] ?? 0) & 0xff;
  return x;
}

function assertByte(value: number, what: string): void {
  if (!Number.isInteger(value) || value < 0 || value > 0xff) throw new RangeError(`${what} must be an integer 0-255, got ${value}`);
}

/** Build a 6-byte command frame `03 0A d1 d2 d3 xor`. */
export function buildCommand(d1: number, d2 = 0, d3 = 0): Uint8Array<ArrayBuffer> {
  assertByte(d1, 'Command id');
  assertByte(d2, 'Command DATA2');
  assertByte(d3, 'Command DATA3');
  const frame = new Uint8Array(BOOKOO_COMMAND_LENGTH);
  frame.set([BOOKOO_PRODUCT_SCALE, BOOKOO_COMMAND_TYPE, d1, d2, d3]);
  frame[5] = xorChecksum(frame, 5);
  return frame;
}

function assertIntInRange(value: number, min: number, max: number, what: string): void {
  if (!Number.isInteger(value) || value < min || value > max) throw new RangeError(`${what} must be an integer ${min}-${max}, got ${value}`);
}

/**
 * Named command builders. Note the parameter positions differ: flow smoothing uses DATA2, beep and
 * auto-off use DATA3 (per the current official doc, hardware-verified by third parties).
 */
export const bookooCommands = {
  /** Refused by the scale (silently) during auto-mode running/done and while charging. */
  tare: () => buildCommand(BookooCommandId.tare),
  /** Timer commands only take effect in the scale's timing and ratio modes. */
  startTimer: () => buildCommand(BookooCommandId.startTimer),
  stopTimer: () => buildCommand(BookooCommandId.stopTimer),
  resetTimer: () => buildCommand(BookooCommandId.resetTimer),
  tareAndStartTimer: () => buildCommand(BookooCommandId.tareAndStartTimer),
  setFlowSmoothing: (on: boolean) => buildCommand(BookooCommandId.flowSmoothing, on ? 1 : 0, 0),
  /** 0 = mute, 1-3 = low/medium/high (Ultra fw >= 4 accepts 0-3). */
  setBeep: (level: number) => {
    assertIntInRange(level, 0, 3, 'Beep level');
    return buildCommand(BookooCommandId.beep, 0, level);
  },
  /** Auto-off after 5-30 minutes. */
  setAutoOff: (minutes: number) => {
    assertIntInRange(minutes, 5, 30, 'Auto-off minutes');
    return buildCommand(BookooCommandId.autoOff, 0, minutes);
  },
  /** Powder (dose) weight for the scale's ratio mode, 0.1-999.9 g (Ultra beta fw >= 3.2.4 / fw >= 4.0.0). */
  setPowderWeight: (grams: number) => {
    const tenths = Math.round(grams * 10);
    if (!Number.isFinite(grams) || tenths < 1 || tenths > 9999) throw new RangeError(`Powder weight must be 0.1-999.9 g, got ${grams}`);
    return buildCommand(BookooCommandId.powderWeight, tenths >> 8, tenths & 0xff);
  },
  /** Reset the auto-off countdown (Ultra fw >= 4.0.1; undocumented on other firmware). */
  keepAwake: () => buildCommand(BookooCommandId.keepAwake),
  /** Switch the scale off: `03 0A 15 00 00 1C` (Ultra fw >= 4.0.0; ignored while charging; no reply). */
  shutdown: () => buildCommand(BookooCommandId.shutdown),
} as const;

// ---------------------------------------------------------------- incoming packets

export type BookooUnit = 'g' | 'oz' | 'unknown';
export type BookooAutoEvent = 'stopped' | 'started' | 'ready' | 'exitReady' | 'exitDone' | 'unknown';

export interface BookooWeightPacket {
  type: 'weight';
  /** The scale's own stopwatch (0 unless its timer runs). */
  timerMs: number;
  /** Display unit on the scale. The weight value is always grams. */
  unit: BookooUnit;
  weightG: number;
  /** Flow rate computed by the scale (g/s). */
  flowGs: number;
  batteryPct: number;
  /** Configured auto-off time in minutes. */
  standbyMin: number;
  buzzer: number;
  flowSmoothing: boolean;
}

export interface BookooPowderPacket {
  type: 'powder';
  weightG: number;
}

export interface BookooEventPacket {
  type: 'event';
  event: BookooAutoEvent;
  eventCode: number;
  timerMs: number;
  weightG: number;
  /** Average flow (timing mode) or liquid/powder ratio (ratio mode); the packet doesn't say which. */
  result: number;
}

/** Well-formed frame of a type we don't decode (e.g. replies to undocumented queries). */
export interface BookooUnknownPacket {
  type: 'unknown';
  packetType: number;
  bytes: Uint8Array;
}

export type BookooPacket = BookooWeightPacket | BookooPowderPacket | BookooEventPacket | BookooUnknownPacket;

export type BookooParseFailure = 'length' | 'header' | 'checksum';

export type BookooParseResult = { ok: true; packet: BookooPacket } | { ok: false; reason: BookooParseFailure; bytes: Uint8Array };

/**
 * How incoming checksums are validated.
 * - 'strict': byte 19 must be the XOR of bytes 0-18 (current official doc; the default).
 * - 'allowZero': also accept a 00 checksum on the documented frame types (the first Ultra doc showed byte 19 as 00).
 * - 'ignore': accept any checksum on the documented frame types (explicit user opt-in only).
 * Undocumented frame types always need a valid XOR. BLE's link-layer CRC already guards the payload.
 */
export type BookooChecksumMode = 'strict' | 'allowZero' | 'ignore';

export interface BookooParseOptions {
  checksum?: BookooChecksumMode;
}

/** The documented incoming frame types (weight, auto-mode event, powder). */
export function isDocumentedFrameType(type: number | undefined): boolean {
  return type === BookooPacketType.weight || type === BookooPacketType.event || type === BookooPacketType.powder;
}

function checksumAccepted(b: Uint8Array, mode: BookooChecksumMode): boolean {
  const sum = b[BOOKOO_PACKET_LENGTH - 1];
  if (xorChecksum(b, BOOKOO_PACKET_LENGTH - 1) === sum) return true;
  if (mode === 'strict' || !isDocumentedFrameType(b[1])) return false;
  return mode === 'ignore' || sum === 0;
}

const AUTO_EVENTS: Record<number, BookooAutoEvent> = {
  0x00: 'stopped',
  0x01: 'started',
  0x02: 'ready',
  0x03: 'exitReady',
  0x04: 'exitDone',
};

function toBytes(data: DataView | Uint8Array): Uint8Array {
  return data instanceof Uint8Array ? data : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
}

/** Sign bytes are ASCII '+' / '-'; anything but '-' (some firmware sends 0x00) counts as positive. */
function sign(b: number | undefined): 1 | -1 {
  return b === SIGN_MINUS ? -1 : 1;
}

function u16(b: Uint8Array, o: number): number {
  return ((b[o] ?? 0) << 8) | (b[o + 1] ?? 0);
}

function u24(b: Uint8Array, o: number): number {
  return ((b[o] ?? 0) << 16) | ((b[o + 1] ?? 0) << 8) | (b[o + 2] ?? 0);
}

/** Avoid -0 for zero values with a '-' sign byte. */
function signed(s: 1 | -1, magnitude: number): number {
  return magnitude === 0 ? 0 : s * magnitude;
}

/** Decode one 20-byte frame, reporting why it was rejected. */
export function parsePacketDetailed(data: DataView | Uint8Array, options: BookooParseOptions = {}): BookooParseResult {
  const b = toBytes(data);
  if (b.length !== BOOKOO_PACKET_LENGTH) return { ok: false, reason: 'length', bytes: b };
  if (b[0] !== BOOKOO_PRODUCT_SCALE) return { ok: false, reason: 'header', bytes: b };
  if (!checksumAccepted(b, options.checksum ?? 'strict')) return { ok: false, reason: 'checksum', bytes: b };

  const type = b[1] ?? 0;
  switch (type) {
    case BookooPacketType.weight: {
      const unitByte = b[5];
      return {
        ok: true,
        packet: {
          type: 'weight',
          timerMs: u24(b, 2),
          unit: unitByte === 0x01 ? 'g' : unitByte === 0x02 ? 'oz' : 'unknown',
          weightG: signed(sign(b[6]), u24(b, 7) / 100),
          flowGs: signed(sign(b[10]), u16(b, 11) / 100),
          batteryPct: b[13] ?? 0,
          standbyMin: u16(b, 14) / 10,
          buzzer: b[16] ?? 0,
          flowSmoothing: b[17] === 1,
        },
      };
    }
    case BookooPacketType.powder:
      return { ok: true, packet: { type: 'powder', weightG: signed(sign(b[2]), u24(b, 3) / 100) } };
    case BookooPacketType.event: {
      const code = b[2] ?? 0;
      return {
        ok: true,
        packet: {
          type: 'event',
          event: AUTO_EVENTS[code] ?? 'unknown',
          eventCode: code,
          timerMs: u24(b, 3),
          weightG: signed(sign(b[6]), u24(b, 7) / 100),
          result: signed(sign(b[10]), u16(b, 11) / 100),
        },
      };
    }
    default:
      return { ok: true, packet: { type: 'unknown', packetType: type, bytes: b.slice() } };
  }
}

/** Decode one 20-byte frame; null when the length, product byte or checksum is wrong. */
export function parsePacket(data: DataView | Uint8Array, options?: BookooParseOptions): BookooPacket | null {
  const r = parsePacketDetailed(data, options);
  return r.ok ? r.packet : null;
}

/**
 * A single notification can carry several back-to-back frames on some stacks. Split exact multiples
 * of 20 bytes; anything else is returned as-is (and will fail parsing with reason 'length').
 */
export function splitFrames(bytes: Uint8Array): Uint8Array[] {
  if (bytes.length <= BOOKOO_PACKET_LENGTH || bytes.length % BOOKOO_PACKET_LENGTH !== 0) return [bytes];
  const frames: Uint8Array[] = [];
  for (let o = 0; o < bytes.length; o += BOOKOO_PACKET_LENGTH) frames.push(bytes.subarray(o, o + BOOKOO_PACKET_LENGTH));
  return frames;
}

// ---------------------------------------------------------------- encoding (simulator + tests)

export interface WeightPacketFields {
  weightG: number;
  timerMs?: number;
  unit?: 'g' | 'oz';
  flowGs?: number;
  batteryPct?: number;
  standbyMin?: number;
  buzzer?: number;
  flowSmoothing?: boolean;
}

function clampInt(v: number, max: number): number {
  return Math.min(max, Math.max(0, Math.round(v)));
}

/** Encode a type 0x0B weight frame exactly like the scale sends it (values clamped to the field ranges). */
export function encodeWeightPacket(f: WeightPacketFields): Uint8Array<ArrayBuffer> {
  const b = new Uint8Array(BOOKOO_PACKET_LENGTH);
  const timer = clampInt(f.timerMs ?? 0, 0xffffff);
  const weight = clampInt(Math.abs(f.weightG) * 100, 0xffffff);
  const flow = clampInt(Math.abs(f.flowGs ?? 0) * 100, 0xffff);
  const standby = clampInt((f.standbyMin ?? 5) * 10, 0xffff);
  b.set([
    BOOKOO_PRODUCT_SCALE,
    BookooPacketType.weight,
    (timer >> 16) & 0xff,
    (timer >> 8) & 0xff,
    timer & 0xff,
    f.unit === 'oz' ? 0x02 : 0x01,
    f.weightG < 0 && weight > 0 ? SIGN_MINUS : SIGN_PLUS,
    (weight >> 16) & 0xff,
    (weight >> 8) & 0xff,
    weight & 0xff,
    (f.flowGs ?? 0) < 0 && flow > 0 ? SIGN_MINUS : SIGN_PLUS,
    (flow >> 8) & 0xff,
    flow & 0xff,
    clampInt(f.batteryPct ?? 100, 100),
    (standby >> 8) & 0xff,
    standby & 0xff,
    clampInt(f.buzzer ?? 0, 0xff),
    f.flowSmoothing ? 1 : 0,
    0,
  ]);
  b[19] = xorChecksum(b, 19);
  return b;
}

/** "03 0a 01 00 00 08" — for logs and the debug view. */
export function toHex(bytes: ArrayLike<number>): string {
  return Array.from({ length: bytes.length }, (_, i) => ((bytes[i] ?? 0) & 0xff).toString(16).padStart(2, '0')).join(' ');
}
